import React from 'react';
import { useLoops } from '@/hooks/use-loops';
import { useReblogAnnotations } from '@/hooks/use-reblogs';
import { AnimatePresence, motion } from 'framer-motion';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { TransitionProvider } from '@/components/transitions/TransitionContext';
import TransitionLoopFeed from '@/components/transitions/TransitionLoopFeed';
import TransitionPost from '@/components/transitions/TransitionPost';
import AppLayout from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import usePostTransition from '@/hooks/use-post-transition';
import { useLoopActions } from '@/hooks/use-loops';
import { useReblogActions } from '@/hooks/use-reblogs';

const LoopsWithTransitions: React.FC = () => {
  // Fetch loops data
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = useLoops('chronological');

  // Flatten loops data
  const loops = React.useMemo(() => 
    data?.pages.flatMap(page => page) || [], 
    [data]
  );

  // Track engaged loops
  const [engagedLoops, setEngagedLoops] = React.useState<Set<string>>(new Set());
  
  // Hook for handling post transitions
  const { activeLoop, isPostOpen, openPost, closePost } = usePostTransition();
  
  // Hook for loop actions
  const { likeLoop, unlikeLoop } = useLoopActions();
  const { reblog, quote } = useReblogActions();
  
  // Fetch reblog annotations for visible loops
  const postIds = React.useMemo(() => loops.map(l => l.id), [loops]);
  const { data: annotationsMap } = useReblogAnnotations(postIds);
  
  // Handle engagement
  const handleEngage = (loopId: string) => {
    console.log('Engaging with loop:', loopId);
    setEngagedLoops(prev => new Set([...prev, loopId]));
  };
  
  // Add debugging for post click
  const handlePostClick = (loop: any) => {
    console.log('Post clicked:', loop.id);
    openPost(loop);
  };
  
  // Handle like
  const handleLike = (loop: any) => {
    if (loop.is_liked) {
      unlikeLoop(loop.id);
    } else {
      likeLoop(loop.id);
    }
  };
  
  // Handle reblog
  const handleReblog = (loop: any) => {
    reblog(loop.id);
  };
  
  // Show loading state
  if (isLoading) {
    return <LoadingFallback />;
  }
  
  // Show error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
        <p className="text-muted-foreground mb-4">Failed to load loops</p>
        <Button onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <TransitionProvider>
      <AppLayout
        showHeader={true}
        pageTitle="Loops"
      >
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key="feed-view"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {loops.map((loop) => (
                <TransitionLoopFeed
                  key={loop.id}
                  loop={loop}
                  onEngage={handleEngage}
                  isEngaged={engagedLoops.has(loop.id) || !!loop.is_liked}
                  annotations={annotationsMap?.[loop.id]}
                  onPostClick={handlePostClick}
                  onLike={handleLike}
                  onReblog={handleReblog}
                />
              ))}
              
              {/* Load more button */}
              {hasNextPage && (
                <div className="flex justify-center pt-6">
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    variant="outline"
                    className="border-[#343536] bg-[#272729] hover:bg-[#2D2D2F] text-[#D7DADC]"
                  >
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          
          {/* Expanded post view with transitions */}
          <TransitionPost
            isOpen={isPostOpen}
            onClose={closePost}
            loop={activeLoop}
          />
        </div>
      </AppLayout>
    </TransitionProvider>
  );
};

export default LoopsWithTransitions;
